import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { FileText, Lock } from 'lucide-react';
import type { QuoteDocument } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { QuoteBlocksView } from './QuoteBlockEditor.tsx';

// The quote as it appears ON THE TICKET: read-only, and without the
// money unless the reader is a quote agent.
//
// Editing lives on the Quotes board, not here. Two reasons. Quoting is
// one job done in one place, and putting the editor on the ticket was
// what made "create a quote" mean "fill in a ticket form first". And the
// scope a technician needs to read is not the same artefact as the
// priced document being negotiated — this shows the first and withholds
// the second.

interface Props {
  ticketId: string;
  /** Lets the ticket page lock the quote fields on its Details panel. */
  onDocumentPresenceChange?: (present: boolean) => void;
}

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function QuoteScopePanel({
  ticketId,
  onDocumentPresenceChange,
}: Props): React.ReactElement {
  const [doc, setDoc] = useState<QuoteDocument | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ document: QuoteDocument | null; canWrite: boolean }>(
        `/api/tickets/${ticketId}/quote-document`,
      );
      setDoc(data.document);
      setCanWrite(data.canWrite);
      onDocumentPresenceChange?.(data.document !== null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the scope of work.');
    } finally {
      setLoading(false);
    }
  }, [ticketId, onDocumentPresenceChange]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p className="text-sm text-[var(--color-muted)]">Loading the scope of work…</p>;
  }

  if (error) {
    return <p className="text-sm text-[var(--color-danger)]">{error}</p>;
  }

  if (!doc) {
    return (
      <div className="rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-white p-5 text-center">
        <FileText className="mx-auto h-5 w-5 text-[var(--color-muted)]" aria-hidden="true" />
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          Nothing has been scoped for this quote yet.
        </p>
        {canWrite && (
          <Link
            to={`/admin/quotes/${ticketId}`}
            className="mt-2 inline-block text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            Write the quote →
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-header)]">{doc.title}</h3>
          <p className="text-xs text-[var(--color-muted)]">
            {doc.totalHours.toLocaleString('en-US', { maximumFractionDigits: 2 })} hours
            {doc.pricingVisible && doc.totalAmountUsd !== null
              ? ` · ${money(doc.totalAmountUsd)}`
              : ''}
          </p>
        </div>
        {canWrite && (
          <Link
            to={`/admin/quotes/${ticketId}`}
            className="text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            Open in Quotes →
          </Link>
        )}
      </div>

      {doc.intro && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-body)]">{doc.intro}</p>
      )}

      <div className="space-y-3">
        {doc.sections
          .filter((section) => !section.collapsible)
          .map((section) => (
            <div key={section.id}>
              <h4 className="text-sm font-semibold text-[var(--color-header)]">{section.heading}</h4>
              <div className="mt-1">
                <QuoteBlocksView blocks={section.blocks} />
              </div>
            </div>
          ))}
      </div>

      {/* The agreements are terms, not scope - named, not reprinted. */}
      {doc.sections.some((s) => s.collapsible) && (
        <p className="text-xs text-[var(--color-muted)]">
          Signed with:{' '}
          {doc.sections
            .filter((s) => s.collapsible)
            .map((s) => s.heading)
            .join(', ')}
          .
        </p>
      )}

      <div className="overflow-x-auto rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white">
        <table className="w-full min-w-[420px] text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <th className="px-3 py-2 font-semibold">Work</th>
              <th className="px-3 py-2 font-semibold">Qty</th>
              {doc.pricingVisible && <th className="px-3 py-2 text-right font-semibold">Amount</th>}
            </tr>
          </thead>
          <tbody>
            {doc.lineItems.map((item) => (
              <tr key={item.id} className="border-b border-[var(--color-border)] last:border-0">
                <td className="px-3 py-2 text-[var(--color-body)]">{item.description}</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-[var(--color-body)]">
                  {item.quantity} {item.unit}
                </td>
                {doc.pricingVisible && (
                  <td className="px-3 py-2 text-right tabular-nums text-[var(--color-body)]">
                    {item.amountUsd === null ? '—' : money(item.amountUsd)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!doc.pricingVisible && (
        <p className="flex items-start gap-2 text-xs text-[var(--color-muted)]">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Pricing on this quote is visible to quote agents. Hours are shown because they become the
          project budget.
        </p>
      )}

      {doc.closing && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-body)]">{doc.closing}</p>
      )}
    </div>
  );
}
